import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { useEffect, useRef, useState } from 'react'
import FragranceImage from '../collection/FragranceImage'
import { Button, Modal, Spinner } from '../ui'
import { fetchShareCardPayload, recordSharedByMe } from '../../lib/shareCard'
import { prepareFragranceShareCard } from '../../lib/fragranceShareCard'

function loadImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) return reject(new Error('No image source'))
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function primaryImageUrl(fragrance) {
  const value = fragrance?.image
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  return `/cards/${value.replace(/^.*\//, '')}`
}

async function buildImage(payload) {
  if (document.fonts?.load) {
    await Promise.allSettled([
      document.fonts.load('700 74px Montserrat'),
      document.fonts.load('500 34px Montserrat'),
      document.fonts.load('400 34px Montserrat'),
      document.fonts.load('700 26px "Playfair Display"'),
    ])
  }

  const fragrance = payload.fragrance
  const canvas = document.createElement('canvas')
  canvas.width = 1080
  canvas.height = 1350
  const ctx = canvas.getContext('2d')
  const gradient = ctx.createLinearGradient(0, 0, 1080, 1350)
  gradient.addColorStop(0, '#242438')
  gradient.addColorStop(0.7, '#0D0F1A')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // Brand gradient frame: gold → royal purple → rose
  const frame = ctx.createLinearGradient(54, 54, 1026, 1296)
  frame.addColorStop(0, '#D4AF37')
  frame.addColorStop(0.58, '#7B5CFF')
  frame.addColorStop(1, '#D05297')
  ctx.strokeStyle = frame
  ctx.lineWidth = 6
  ctx.strokeRect(54, 54, 972, 1242)

  ctx.fillStyle = 'rgba(212,175,55,0.12)'
  ctx.beginPath()
  ctx.arc(540, 360, 220, 0, Math.PI * 2)
  ctx.fill()

  try {
    const img = await loadImage(primaryImageUrl(fragrance))
    const scale = Math.min(420 / img.width, 520 / img.height)
    const width = img.width * scale
    const height = img.height * scale
    ctx.drawImage(img, (1080 - width) / 2, 145 + (520 - height) / 2, width, height)
  } catch {
    ctx.fillStyle = '#1A1A2E'
    ctx.fillRect(365, 170, 350, 480)
    ctx.fillStyle = '#D4AF37'
    ctx.font = '700 86px Montserrat, sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText((fragrance?.house || 'FO').slice(0, 2).toUpperCase(), 540, 430)
  }

  ctx.textAlign = 'center'
  ctx.fillStyle = '#F5F5F0'
  ctx.font = '700 74px Montserrat, sans-serif'
  wrapText(ctx, fragrance?.name || 'FragranceOS', 540, 760, 860, 82)
  ctx.fillStyle = '#D4AF37'
  ctx.font = '500 34px Montserrat, sans-serif'
  ctx.fillText(fragrance?.house || '', 540, 920)
  ctx.fillStyle = '#c9c9da'
  ctx.font = '400 34px Montserrat, sans-serif'
  wrapText(ctx, `${payload.sharer_name} is wearing this today.`, 540, 1010, 820, 46)
  ctx.fillStyle = '#D4AF37'
  ctx.font = '700 26px "Playfair Display", serif'
  ctx.fillText('FRAGRANCEOS', 540, 1220)
  ctx.fillStyle = 'rgba(245,245,240,0.55)'
  ctx.font = '500 22px Montserrat, sans-serif'
  ctx.fillText('KNOW WHAT TO WEAR, REMEMBER WHY IT MATTERED.', 540, 1262)

  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text || '').split(/\s+/)
  let line = ''
  for (const word of words) {
    const test = line ? `${line} ${word}` : word
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y)
      line = word
      y += lineHeight
    } else {
      line = test
    }
  }
  if (line) ctx.fillText(line, x, y)
}

export default function ShareCardSheet({ open, onClose, fragranceId }) {
  const shareLoopsEnabled = useFeatureFlag('share_loops')
  const fragranceShareCardEnabled = useFeatureFlag('fragrance_share_card')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [payload, setPayload] = useState(null)
  const [loadError, setLoadError] = useState('')
  const downloadRef = useRef(null)

  useEffect(() => {
    if (!open || !fragranceId) return undefined
    let active = true
    setMessage('')
    setLoadError('')
    setPayload(null)
    const prepare = fragranceShareCardEnabled ? prepareFragranceShareCard : fetchShareCardPayload
    prepare(fragranceId)
      .then(data => {
        if (active) setPayload(data)
      })
      .catch(error => {
        if (active) setLoadError(error?.message || 'Could not load this share card.')
      })
    return () => {
      active = false
    }
  }, [open, fragranceId, fragranceShareCardEnabled])

  if (!fragranceId) return null

  const handleShare = async () => {
    if (!payload) return
    setBusy(true)
    setMessage('')
    try {
      if (fragranceShareCardEnabled) {
        const card = payload.payload
        const text = [
          card.name,
          card.concentration,
          card.description,
          card.notes.length > 0 ? `Notes: ${card.notes.join(', ')}` : '',
        ].filter(Boolean).join('\n\n')
        try {
          if (navigator.share) {
            await navigator.share({ title: card.name, text, url: payload.link })
            setMessage('Fragrance card shared.')
          } else {
            await navigator.clipboard.writeText(payload.link)
            setMessage('Fragrance card link copied.')
          }
        } catch (error) {
          if (error?.name === 'AbortError') return
          try {
            await navigator.clipboard.writeText(payload.link)
            setMessage('Fragrance card link copied.')
          } catch {
            setMessage(error?.message || 'Share fallback is not available here.')
          }
        }
        return
      }
      const blob = await buildImage(payload)
      const file = new File([blob], `${payload.fragrance.name || 'fragranceos'}-card.png`, { type: 'image/png' })
      const shareData = {
        title: payload.fragrance.name,
        text: `${payload.sharer_name} is ${payload.status}: ${payload.fragrance.name} by ${payload.fragrance.house}.`,
        url: payload.link,
        files: [file],
      }
      if (navigator.canShare?.({ files: [file] }) && navigator.share) {
        await navigator.share(shareData)
        await recordSharedByMe(payload)
        setMessage('Card shared.')
      } else {
        const objectUrl = URL.createObjectURL(blob)
        downloadRef.current.href = objectUrl
        downloadRef.current.download = file.name
        downloadRef.current.click()
        URL.revokeObjectURL(objectUrl)
        if (shareLoopsEnabled) {
          await navigator.clipboard.writeText(payload.link)
          await recordSharedByMe(payload)
        }
        setMessage(shareLoopsEnabled ? 'Card image saved and link copied.' : 'Card image saved.')
      }
    } catch (e) {
      if (e?.name === 'AbortError') return
      try {
        await navigator.clipboard.writeText(payload.link)
        await recordSharedByMe(payload)
        setMessage('Card link copied.')
      } catch {
        setMessage(e?.message || 'Share fallback is not available here.')
      }
    } finally {
      setBusy(false)
    }
  }

  const copyLink = async () => {
    if (!payload || busy) return
    setBusy(true)
    try {
      await navigator.clipboard.writeText(payload.link)
      if (!fragranceShareCardEnabled) await recordSharedByMe(payload)
      setMessage('Link copied.')
    } catch (error) {
      setMessage(error?.message || 'Could not copy and record this share. Please retry.')
    } finally { setBusy(false) }
  }

  return (
    <Modal open={open} onClose={onClose} title="Share Card" maxWidth="max-w-sm">
      <div className="flex flex-col gap-4">
        {!payload && !loadError && (
          <div className="flex min-h-40 items-center justify-center"><Spinner /></div>
        )}
        {loadError && (
          <p className="rounded-xl border border-red-400/30 bg-red-400/10 p-3 text-sm text-red-200">{loadError}</p>
        )}
        {payload && fragranceShareCardEnabled && (
          <div
            data-testid="share-card-payload"
            data-share-payload={JSON.stringify(payload.payload)}
            className="overflow-hidden rounded-2xl border border-gold/70 bg-charcoal-800"
          >
            <div className="border-b border-gold/20 p-4">
              <p className="break-words font-body text-2xl leading-tight text-cream">{payload.payload.name}</p>
              {payload.payload.concentration && <p className="mt-1 text-xs text-gold">{payload.payload.concentration}</p>}
            </div>
            <div className="space-y-4 p-4">
              <p className="text-sm leading-6 text-cream/70">{payload.payload.description || 'No description is stored for this fragrance yet.'}</p>
              <div className="flex flex-wrap gap-1.5 border-t border-white/10 pt-4">
                {payload.payload.notes.length > 0
                  ? payload.payload.notes.map(note => <span key={note} className="pill pill-note max-w-full break-words text-xs">{note}</span>)
                  : <span className="text-xs text-cream/40">No notes stored yet.</span>}
              </div>
            </div>
          </div>
        )}
        {payload && !fragranceShareCardEnabled && (
          <div
            data-testid="share-card-payload"
            data-share-payload={JSON.stringify(payload)}
            className="rounded-2xl border border-gold/70 bg-gradient-to-br from-charcoal-700 to-charcoal-900 p-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-24 shrink-0"><FragranceImage fragrance={{ name: payload.fragrance.name, house: payload.fragrance.house, card_image: payload.fragrance.image }} /></div>
              <div className="min-w-0">
                <p className="font-body text-lg leading-tight text-cream">{payload.fragrance.name}</p>
                <p className="mt-1 text-xs text-cream/45">{payload.fragrance.house}</p>
                <p className="mt-3 text-xs leading-relaxed text-cream/70">{payload.sharer_name} is wearing this today.</p>
                <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">FragranceOS</p>
              </div>
            </div>
          </div>
        )}
        {message && <p className="text-center text-xs text-sage">{message}</p>}
        <div className="flex gap-3">
          <Button onClick={handleShare} disabled={busy || !payload} className="flex-1">
            {busy ? <><Spinner size="sm" /> Preparing</> : 'Share card'}
          </Button>
          <Button variant="ghost" onClick={copyLink} disabled={busy || !payload} className="flex-1">Copy link</Button>
        </div>
        <a ref={downloadRef} className="hidden">Download card</a>
      </div>
    </Modal>
  )
}
